import React, { useState } from "react";
import hosys from "@/api/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { FireStatusBadge } from "./TechnicalStatusBadge";
import { Flame, Plus, Pencil, AlertTriangle, Upload, FileText, Trash2 } from "lucide-react";

const ELEMENT_TYPES = {
  extintor:          "Extintor",
  bie:               "BIE (Boca incendi equipada)",
  detector_fum:      "Detector de fum",
  detector_gas:      "Detector de gas",
  central_alarma:    "Central d'alarma",
  portes_tallafoc:   "Portes tallafoc",
  senyalitzacio:     "Senyalització",
  llums_emergencia:  "Llums d'emergència",
  rociadors:         "Ruixadors / Rociadors",
  altres:            "Altres",
};

function DaysChip({ dateStr }) {
  if (!dateStr) return <span className="text-xs text-gray-400">—</span>;
  const diff = Math.ceil((new Date(dateStr) - new Date()) / 86400000);
  if (diff < 0)   return <span className="text-xs font-semibold text-red-600">Caducada ({Math.abs(diff)}d)</span>;
  if (diff <= 30) return <span className="text-xs font-semibold text-orange-500">En {diff}d</span>;
  if (diff <= 90) return <span className="text-xs font-semibold text-yellow-600">En {diff}d</span>;
  return <span className="text-xs text-green-600">En {diff}d</span>;
}

function FireForm({ item, venueId, onSave, onClose }) {
  const empty = {
    venue_id: venueId, element_type: "extintor", name: "", location: "",
    brand: "", model: "", serial_number: "", capacity: "",
    installation_date: "", last_revision_date: "", next_revision_date: "",
    revision_frequency_months: 12, maintenance_company: "",
    maintenance_company_phone: "", maintenance_company_email: "",
    certificate_url: "", contract_url: "", report_url: "",
    status: "ok", notes: "", active: true,
  };
  const [form, setForm] = useState(item || empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const uploadFile = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(field);
    const { file_url } = await hosys.integrations.Core.UploadFile({ file });
    set(field, file_url);
    setUploading(null);
  };

  const handleSave = async () => {
    setSaving(true);
    if (item?.id) {
      await hosys.entities.FireProtection.update(item.id, form);
    } else {
      await hosys.entities.FireProtection.create(form);
    }
    setSaving(false);
    onSave();
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{item ? "Editar element" : "Nou element de protecció"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Tipus d'element *</Label>
              <Select value={form.element_type} onValueChange={v => set("element_type", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ELEMENT_TYPES).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Nom / Referència *</Label>
              <Input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Ex: Extintor cuina 1" />
            </div>
            <div>
              <Label>Ubicació</Label>
              <Input value={form.location} onChange={e => set("location", e.target.value)} placeholder="Ex: Cuina, sortida..." />
            </div>
            <div>
              <Label>Capacitat / Classe</Label>
              <Input value={form.capacity} onChange={e => set("capacity", e.target.value)} placeholder="Ex: 6kg ABC" />
            </div>
            <div>
              <Label>Marca</Label>
              <Input value={form.brand} onChange={e => set("brand", e.target.value)} />
            </div>
            <div>
              <Label>Model</Label>
              <Input value={form.model} onChange={e => set("model", e.target.value)} />
            </div>
            <div>
              <Label>Núm. de sèrie</Label>
              <Input value={form.serial_number} onChange={e => set("serial_number", e.target.value)} />
            </div>
            <div>
              <Label>Data instal·lació</Label>
              <Input type="date" value={form.installation_date} onChange={e => set("installation_date", e.target.value)} />
            </div>
            <div>
              <Label>Última revisió</Label>
              <Input type="date" value={form.last_revision_date} onChange={e => set("last_revision_date", e.target.value)} />
            </div>
            <div>
              <Label>Propera revisió</Label>
              <Input type="date" value={form.next_revision_date} onChange={e => set("next_revision_date", e.target.value)} />
            </div>
            <div>
              <Label>Periodicitat (mesos)</Label>
              <Input type="number" value={form.revision_frequency_months} onChange={e => set("revision_frequency_months", Number(e.target.value))} />
            </div>
            <div>
              <Label>Estat</Label>
              <Select value={form.status} onValueChange={v => set("status", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ok">Correcte</SelectItem>
                  <SelectItem value="pending_revision">Revisió pendent</SelectItem>
                  <SelectItem value="expired">Caducada</SelectItem>
                  <SelectItem value="deficient">Deficient</SelectItem>
                  <SelectItem value="inactive">Inactiu</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="border-t pt-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Empresa mantenidora</p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Nom empresa</Label>
                <Input value={form.maintenance_company} onChange={e => set("maintenance_company", e.target.value)} />
              </div>
              <div>
                <Label>Telèfon</Label>
                <Input value={form.maintenance_company_phone} onChange={e => set("maintenance_company_phone", e.target.value)} />
              </div>
              <div>
                <Label>Email</Label>
                <Input value={form.maintenance_company_email} onChange={e => set("maintenance_company_email", e.target.value)} />
              </div>
            </div>
          </div>

          <div className="border-t pt-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Documents</p>
            <div className="grid grid-cols-3 gap-3">
              {[["certificate_url", "Certificat"], ["contract_url", "Contracte"], ["report_url", "Informe revisió"]].map(([field, label]) => (
                <div key={field}>
                  <Label>{label}</Label>
                  {form[field] ? (
                    <div className="flex items-center gap-1 mt-1">
                      <a href={form[field]} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline truncate flex-1">Veure doc.</a>
                      <button onClick={() => set(field, "")} className="text-gray-400 hover:text-red-500"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ) : (
                    <label className="mt-1 flex items-center gap-1.5 cursor-pointer text-xs text-gray-500 hover:text-gray-700 border border-dashed border-gray-300 rounded-lg px-2 py-1.5">
                      {uploading === field ? "Pujant..." : <><Upload className="w-3 h-3" /> Pujar</>}
                      <input type="file" className="hidden" accept=".pdf,.jpg,.png" onChange={e => uploadFile(e, field)} />
                    </label>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel·lar</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#7B2D3F] hover:bg-[#6a2636] text-white">
            {saving ? "Guardant..." : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function FireProtectionTab({ items, onRefresh, venueId }) {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const grouped = items.reduce((acc, el) => {
    const k = el.element_type || "altres";
    if (!acc[k]) acc[k] = [];
    acc[k].push(el);
    return acc;
  }, {});

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-gray-500">{items.length} element{items.length !== 1 ? "s" : ""} registrat{items.length !== 1 ? "s" : ""}</p>
        <Button onClick={() => { setEditing(null); setShowForm(true); }} className="bg-[#7B2D3F] hover:bg-[#6a2636] text-white gap-1.5" size="sm">
          <Plus className="w-4 h-4" /> Nou element
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-14">
          <Flame className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">Cap element registrat</p>
          <p className="text-sm text-gray-400 mb-4">Afegeix extintors, BIE, detectors, etc.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([type, els]) => (
            <div key={type}>
              <p className="text-xs font-bold uppercase tracking-wide text-gray-400 mb-2 px-1">{ELEMENT_TYPES[type] || type}</p>
              <div className="grid gap-2 sm:grid-cols-2">
                {els.map(el => (
                  <div key={el.id} className="bg-white border border-gray-100 rounded-xl p-3 hover:shadow-sm transition-shadow">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-gray-900">{el.name}</span>
                          <FireStatusBadge status={el.status} />
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{el.location}{el.capacity && ` · ${el.capacity}`}</p>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => { setEditing(el); setShowForm(true); }} className="h-7 w-7 p-0">
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      <div>
                        <span className="text-gray-400">Última revisió: </span>
                        <span className="text-gray-700">{el.last_revision_date ? new Date(el.last_revision_date).toLocaleDateString("ca-ES") : "—"}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-gray-400">Pròxima: </span>
                        <span className="text-gray-700">{el.next_revision_date ? new Date(el.next_revision_date).toLocaleDateString("ca-ES") : "—"}</span>
                        <DaysChip dateStr={el.next_revision_date} />
                      </div>
                    </div>
                    {el.maintenance_company && (
                      <p className="text-xs text-gray-400 mt-1">{el.maintenance_company}</p>
                    )}
                    <div className="flex gap-2 mt-2">
                      {el.certificate_url && <a href={el.certificate_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-0.5"><FileText className="w-3 h-3" />Certificat</a>}
                      {el.report_url && <a href={el.report_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-0.5"><FileText className="w-3 h-3" />Informe</a>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <FireForm
          item={editing}
          venueId={venueId}
          onSave={() => { setShowForm(false); setEditing(null); onRefresh(); }}
          onClose={() => { setShowForm(false); setEditing(null); }}
        />
      )}
    </div>
  );
}